import { useEffect, useState } from "react";
import api from "@/lib/api";
import { money } from "@/lib/format";

// Quick-reference list of the miscellaneous add-on items available in a module.
// Mirrors the Materials table look (name + "miscellaneous · CODE" subtitle, module chips,
// supplier, blue price, stock with reorder point) and keeps a Description column.
// `groups` (optional): [{ label, match: [subtype,...] }] to organize items by LF subtype.
export function MiscListTab({ module, groups }) {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => {
    api.get("/misc-materials", { params: { module } }).then((r) => setItems(r.data || [])).catch(() => setItems([]));
  }, [module]);

  const searched = items.filter((it) => !q.trim() || `${it.name || ""} ${it.code || ""}`.toLowerCase().includes(q.trim().toLowerCase()));
  const grouped = groups
    ? groups.map((g) => ({
        label: g.label,
        items: searched.filter((it) => {
          const subs = it.lf_subtypes || [];
          if (!subs.length) return true;
          return g.match.some((k) => subs.includes(k));
        }),
      }))
    : [{ label: "All items", items: searched }];

  return (
    <div className="space-y-4" data-testid="misc-list-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-xs text-slate-500">Reference view · miscellaneous add-ons available in this module. Manage them in <a href="/materials" className="text-[#2495D3] hover:underline">Materials</a>.</p>
        <div className="relative">
          <input data-testid="misc-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or code…"
            className="text-xs rounded-full pl-8 pr-3 py-1.5 border border-slate-200 bg-white text-slate-700 w-56 focus:outline-none focus:ring-1 focus:ring-[#2495D3]" />
          <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
          {q && <button onClick={() => setQ("")} data-testid="misc-search-clear" className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">×</button>}
        </div>
      </div>
      {grouped.map((g) => (
        <div key={g.label} data-testid={`misc-group-${g.label}`}>
          <h4 className="text-[11px] font-mono uppercase tracking-widest text-slate-500 mb-2">{g.label}</h4>
          <div className="border border-slate-200 rounded-xl overflow-hidden bg-white overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-[10px] font-mono uppercase tracking-widest text-slate-500">
                  <th className="text-left px-4 py-2.5">Material</th>
                  <th className="text-left px-4 py-2.5">Supplier</th>
                  <th className="text-right px-4 py-2.5">Unit cost</th>
                  <th className="text-right px-4 py-2.5">Retail</th>
                  <th className="text-right px-4 py-2.5">Wholesale</th>
                  <th className="text-center px-4 py-2.5">Stock</th>
                  <th className="text-left px-4 py-2.5">Description</th>
                </tr>
              </thead>
              <tbody>
                {g.items.map((it) => {
                  const low = (it.stock_qty || 0) <= (it.reorder_point || 0);
                  return (
                  <tr key={it.id} data-testid="misc-list-row" className={`border-b border-slate-100 hover:bg-slate-50 ${low ? "bg-red-50/60" : ""}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium flex items-center gap-2">
                        {it.name}
                        {low && <span className="text-[10px] font-semibold text-red-700 bg-red-100 rounded px-1.5 py-0.5" data-testid="misc-lowstock-badge">LOW STOCK</span>}
                      </div>
                      <div className="text-[11px] text-slate-400 font-mono">miscellaneous{it.code ? ` · ${it.code}` : ""}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-700">{it.supplier_company || "—"}</td>
                    <td className="px-4 py-3 text-right num">{money(it.unit_cost)}</td>
                    <td className="px-4 py-3 text-right num text-[#2495D3]">{money(it.sell_price)}</td>
                    <td className="px-4 py-3 text-right num text-slate-600" data-testid="misc-list-wholesale">{money(it.wholesale_price)}</td>
                    <td className="px-4 py-3 text-center" data-testid="misc-list-stock">
                      <div className={`num font-semibold ${low ? "text-red-600" : "text-slate-700"}`}>{Math.round(it.stock_qty || 0)} {it.unit}</div>
                      <div className="text-[10px] text-slate-400 num">rp {it.reorder_point || 0}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-500">{it.description || "—"}</td>
                  </tr>
                  );
                })}
                {g.items.length === 0 && <tr><td colSpan={7} className="px-4 py-6 text-center text-slate-400">{q.trim() ? "No items match your search." : "No miscellaneous items in this group."}</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}
